import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/lib/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";

const IDLE_MS = 15 * 60 * 1000; // 15 minutes — HIPAA-friendly default
const WARN_MS = 60 * 1000;       // warn 60s before logout
const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll"];

/**
 * Auto-signs the user out after 15 minutes of inactivity. Warns them 60 seconds beforehand.
 * Skipped on public pages (login / privacy).
 */
export default function useIdleTimeout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const idleRef = useRef(null);
  const warnRef = useRef(null);
  const warnedRef = useRef(false);

  const isProtected = !!user && !location.pathname.match(/^\/(login|provider-login|privacy|accept-invite|request-provider-access|onboarding)/);

  const clearAll = useCallback(() => {
    if (idleRef.current) clearTimeout(idleRef.current);
    if (warnRef.current) clearTimeout(warnRef.current);
    warnedRef.current = false;
  }, []);

  const reset = useCallback(() => {
    if (!isProtected) return;
    clearAll();
    warnRef.current = setTimeout(() => {
      warnedRef.current = true;
      toast.warning("You'll be signed out in 1 minute for security.", {
        id: "idle-warn", duration: WARN_MS, description: "Move the mouse or tap anywhere to stay signed in.",
      });
    }, IDLE_MS - WARN_MS);
    idleRef.current = setTimeout(async () => {
      await logout?.();
      toast.info("You've been signed out due to inactivity.", { duration: 5000 });
      navigate("/login", { replace: true });
    }, IDLE_MS);
  }, [isProtected, clearAll, logout, navigate]);

  useEffect(() => {
    if (!isProtected) { clearAll(); return; }
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, reset));
      clearAll();
    };
  }, [isProtected, reset, clearAll]);
}
